import { useState, useEffect } from 'react';
import { TrendingDown, AlertCircle } from 'lucide-react';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';

const LoanRefinanceForm = ({ member, onSuccess, onCancel }) => {
  const [eligibleLoans, setEligibleLoans] = useState([]);
  const [selectedLoans, setSelectedLoans] = useState([]);
  const [loanTypes, setLoanTypes] = useState([]);
  const [formData, setFormData] = useState({
    refinance_type: 'consolidation',
    new_loan_type_id: '',
    new_interest_rate: '',
    new_duration: '',
    cash_out_amount: 0,
    reason: '',
    notes: '',
  });
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const refinanceTypes = [
    { value: 'consolidation', label: 'Consolidation', description: 'Combine multiple loans into one' },
    { value: 'rate_reduction', label: 'Rate Reduction', description: 'Get lower interest rate' },
    { value: 'term_extension', label: 'Term Extension', description: 'Longer repayment period' },
    { value: 'cash_out', label: 'Cash Out', description: 'Consolidate + get extra cash' },
  ];

  useEffect(() => {
    fetchEligibleLoans();
    fetchLoanTypes();
  }, [member]);

  useEffect(() => {
    if (selectedLoans.length > 0 && formData.new_interest_rate && formData.new_duration) {
      calculateRefinance();
    }
  }, [selectedLoans, formData.new_interest_rate, formData.new_duration, formData.cash_out_amount]);

  const fetchEligibleLoans = async () => {
    try {
      const response = await api.get(`/loan-refinances/member/${member.id}/eligible`);
      setEligibleLoans(response.data.data);
    } catch (error) {
      console.error('Error fetching eligible loans:', error);
    }
  };

  const fetchLoanTypes = async () => {
    try {
      const response = await api.get('/loan-type');
      setLoanTypes(response.data.data || response.data);
    } catch (error) {
      console.error('Error fetching loan types:', error);
    }
  };

  const calculateRefinance = async () => {
    if (selectedLoans.length === 0) return;

    setCalculating(true);
    try {
      const response = await api.post('/loan-refinances/calculate', {
        loan_ids: selectedLoans,
        new_interest_rate: formData.new_interest_rate,
        new_duration: formData.new_duration,
        cash_out_amount: formData.cash_out_amount || 0,
      });
      setCalculation(response.data.data);
    } catch (error) {
      console.error('Calculation error:', error);
    } finally {
      setCalculating(false);
    }
  };

  const handleLoanToggle = (loanId) => {
    setSelectedLoans(prev => 
      prev.includes(loanId) 
        ? prev.filter(id => id !== loanId)
        : [...prev, loanId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedLoans.length === 0) {
      toast.error('Please select at least one loan to refinance');
      return;
    }

    setLoading(true);
    try {
      await api.post('/loan-refinances', {
        member_id: member.id,
        old_loan_ids: selectedLoans,
        ...formData,
      });
      
      toast.success('Refinance request created successfully');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create refinance request');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const totalSelectedBalance = eligibleLoans
    .filter(loan => selectedLoans.includes(loan.id))
    .reduce((sum, loan) => sum + parseFloat(loan.loan_balance), 0);

  return (
    <div className="bg-white rounded-lg p-6 max-h-[90vh] overflow-y-auto">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Refinance Loans</h3>
        <p className="text-sm text-gray-600">
          Consolidate multiple loans or get better terms for {member.name}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Refinance Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Refinance Type *
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {refinanceTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData({ ...formData, refinance_type: type.value })}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  formData.refinance_type === type.value
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="font-semibold text-gray-900">{type.label}</div>
                <div className="text-sm text-gray-600 mt-1">{type.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Select Loans */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select Loans to Refinance *
          </label>
          {eligibleLoans.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-600">No eligible loans found for refinancing</p>
            </div>
          ) : (
            <div className="space-y-2">
              {eligibleLoans.map((loan) => (
                <label
                  key={loan.id}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    selectedLoans.includes(loan.id)
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedLoans.includes(loan.id)}
                    onChange={() => handleLoanToggle(loan.id)}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900">{loan.loan_no}</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(loan.loan_balance)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600 mt-1">
                      <span>{loan.loan_type?.name}</span>
                      <span>{loan.interest_rate}% • {loan.duration} months</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
          {selectedLoans.length > 0 && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-blue-700">Total Balance Selected:</span>
                <span className="font-semibold text-blue-900">{formatCurrency(totalSelectedBalance)}</span>
              </div>
            </div>
          )}
        </div>

        {/* New Loan Terms */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Loan Type *
            </label>
            <select
              value={formData.new_loan_type_id}
              onChange={(e) => setFormData({ ...formData, new_loan_type_id: e.target.value })}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select loan type</option>
              {loanTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Interest Rate (%) *
            </label>
            <input
              type="number"
              value={formData.new_interest_rate}
              onChange={(e) => setFormData({ ...formData, new_interest_rate: e.target.value })}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="8.0"
              step="0.01"
              min="0"
              max="100"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Duration (months) *
            </label>
            <input
              type="number"
              value={formData.new_duration}
              onChange={(e) => setFormData({ ...formData, new_duration: e.target.value })}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="12"
              min="1"
              required
            />
          </div>

          {formData.refinance_type === 'cash_out' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cash Out Amount
              </label>
              <input
                type="number"
                value={formData.cash_out_amount}
                onChange={(e) => setFormData({ ...formData, cash_out_amount: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          )}
        </div>

        {/* Calculation Results */}
        {calculation && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-4 flex items-center">
              <TrendingDown className="w-5 h-5 mr-2" />
              Refinance Savings
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-green-700">Old Monthly Payment</p>
                <p className="font-semibold text-green-900">{formatCurrency(calculation.old_loans.monthly_payment)}</p>
              </div>
              <div>
                <p className="text-green-700">New Monthly Payment</p>
                <p className="font-semibold text-green-900">{formatCurrency(calculation.new_loan.monthly_payment)}</p>
              </div>
              <div>
                <p className="text-green-700">Monthly Savings</p>
                <p className="font-semibold text-green-900">{formatCurrency(calculation.savings.monthly_payment)}</p>
              </div>
              <div>
                <p className="text-green-700">Old Total Interest</p>
                <p className="font-semibold text-green-900">{formatCurrency(calculation.old_loans.total_interest)}</p>
              </div>
              <div>
                <p className="text-green-700">New Total Interest</p>
                <p className="font-semibold text-green-900">{formatCurrency(calculation.new_loan.interest)}</p>
              </div>
              <div>
                <p className="text-green-700">Interest Savings</p>
                <p className="font-semibold text-green-900">{formatCurrency(calculation.savings.interest)}</p>
              </div>
            </div>
            {formData.cash_out_amount > 0 && (
              <div className="mt-3 pt-3 border-t border-green-300">
                <div className="flex items-center justify-between">
                  <span className="text-green-700">Cash Out to Member:</span>
                  <span className="font-semibold text-green-900">{formatCurrency(calculation.new_loan.cash_out)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for Refinance
          </label>
          <textarea
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="2"
            placeholder="Why is this refinance needed?"
          />
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900">Important</p>
              <p className="text-sm text-yellow-800 mt-1">
                This refinance request will create a new loan and mark the selected loans as refinanced once approved. The process is irreversible after approval.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            disabled={loading || calculating || selectedLoans.length === 0}
          >
            {loading ? 'Creating...' : 'Create Refinance Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoanRefinanceForm;
